import {
    TAiImportFieldsMapping,
    TImportFileType,
    TImportGoogleContactsData,
    TImportModalAttributeCheckData,
    TImportModalTagCheckData
} from '~/apps/main/ImportModal/ImportModal.types';
import {
    TExistsContacts,
    TImportGoogleContactsTaskData,
    TImportTaskData,
    TServerImportType
} from '~/apps/main/ImportModal/server-types';
import {
    IExportReportParams,
    ISummaryReportParams
} from '~/components/Reports/types/reports.types';
import {
    IEmailAttachment,
    IEmailAttachmentProps,
    IVendorEmail,
    TFilterTypes
} from '~/types/model.types';
import {
    IPaymentVariableRefunded,
    IUserPipelineStageResponse,
    IWorkspaceAdjustment
} from '~/types/server.types';
import {
    ICreatePayLinkRequest,
    IDeletePayLinkRequest,
    IPayLink
} from '~/modules/PayLinks/pay-link.types';
import {
    ICreatePrismmOccasionDetails,
    IEventPrismmOccasion
} from '~/apps/main/Prismm/prismm.types';
import type { getImportModalAiFieldDescription } from '~/apps/main/ImportModal/services/import-modal-ai-field-description';
import type { IResumeExecutionTaskResult } from '@honeybook/automations-core';

export type TCPTActionReturnType<T extends ClientPendingTaskType> = {
    taskId: string;
    promise: ClientPendingTask[T]['returnType'];
};

interface IAutoSuggestEditTaskData {
    edit_id: string;
    suggestion_id: string;
    option_id?: string;
}

interface IAutoSuggestTaskData {
    edit_id: string;
    subject_id: string;
    context_name: string;
    subject_context: string;
    subject_context_id: string;
    request_free_input_text?: string;
    input_email_body?: string;
    input_email_subject?: string;
    option_id?: string;
    additional_context?: {
        smart_file?: string;
    };
}

export interface IClientPendingTaskNode {
    canceled: boolean;
    onUpdateProgress?: (pendingTaskProgress: any) => void;
    pollCount: number;
    promise: Promise<any>;
    reject: (reason?: any) => void;
    resolve: (value: unknown) => void;
    taskId: string;
    taskType: ClientPendingTaskType;
}

export enum PendingTaskStatus {
    Pending = 0,
    Started = 1,
    Finished = 2,
    Aborted = 3
}

export enum ClientPendingTaskType {
    AIImportFieldsMapping = 'ai_fields_mapping_for_import_worker',
    GenerateInvoicePdf = 'generate_invoice_pdf',
    GenerateReport = 'generate_report',
    PublishFlow = 'publish_flow',
    RefundPayment = 'refund_payment',
    ShareFlow = 'share_flow',
    ShareLeadForm = 'share_lead_form',
    DeleteContacts = 'delete_contacts',
    DeleteWorkspaces = 'delete_workspaces',
    SendBatchEmail = 'send_batch_email',
    SendBulkEmailToWorkspaces = 'send_bulk_email_to_workspaces',
    AutoSuggestEdit = 'auto_suggest_edit',
    AutoSuggest = 'auto_suggest',
    SendWorkspaceMessage = 'send_workspace_message',
    HomeData = 'home_data',
    CustomizePipelineStages = 'change_pipeline_stages',
    ArchiveWorkspaces = 'archive_workspaces',
    MoveWorkspacesStage = 'move_workspaces',
    AssignWorkspaceToTeamMember = 'assign_team_member',
    DeleteTeamMemberFromCompany = 'remove_team_member',
    BulkAddToWorkspaces = 'bulk_add_member_to_workspaces',
    BulkAddTagsToEntities = 'bulk_add_tags_to_entities',
    BulkRemoveTagsFromEntities = 'bulk_remove_tags_from_entities',
    BatchGoogleContactsImport = 'batch_contacts_import_worker',
    CheckImportData = 'check_projects_data_before_import',
    BatchImport = 'batch_projects_import',
    FetchGoogleContactsForImport = 'fetch_google_contacts_for_import',
    InitN8NUser = 'init_n8n_user',
    SendRescheduleProjectEmail = 'send_reschedule_project_email',
    CreatePayLink = 'create_pay_link',
    DeletePayLink = 'delete_pay_link',
    sendFile = 'send_file',
    SharePayLink = 'share_pay_link',
    ReactivatePayLink = 'reactivate_pay_link',
    CreateNewPrismmOccasion = 'create_new_prismm_occasion',
    NewAutomationsCrenetialsCreation = 'new_automations_credentials_creation',
    ApproveMemberCompanyInvite = 'approve_member_company_invite',
    ExportCanvaDesigns = 'export_canva_designs',
    SyncQbPayment = 'sync_qb_payment',
    StartTestAutomation = 'start_automation_test',
    ResumeExecutionNode = 'resume_execution_node'
}

export type SendRescheduleProjectEmailData = {
    workspace_id: string;
    file_ids: string[];
    recipients_ids: string[];
    email: { subject: string; html_body: string; email_type: string };
};

export interface ClientPendingTask
    extends Record<
        ClientPendingTaskType,
        {
            taskData: any;
            onUpdateProgress?: (props: any) => any;
            returnType: any;
        }
    > {
    [ClientPendingTaskType.AIImportFieldsMapping]: {
        taskData: {
            file_import_context: TImportFileType;
            file_raw_data: string[][];
            exist_system_fields: ReturnType<
                typeof getImportModalAiFieldDescription
            >;
            headers_start_index: number;
        };
        onUpdateProgress: undefined;
        returnType: { fields_mapping: TAiImportFieldsMapping[] } | null;
    };

    [ClientPendingTaskType.CheckImportData]: {
        taskData: {
            context_source: Exclude<TServerImportType, 'google'>;
            contacts_by_email: {
                [email: string]: {
                    organization_name: string | undefined;
                };
            };
            lead_source_labels?: string[];
            project_type_labels?: string[];
            stage_names?: string[];
            tag_names?: string[];
            contact_custom_field_names?: string[];
            project_custom_field_names?: string[];
        };
        onUpdateProgress: undefined;
        returnType: {
            exists_contacts: TExistsContacts;
            lead_sources_data: TImportModalAttributeCheckData | null;
            project_types_data: TImportModalAttributeCheckData | null;
            stages_data: TImportModalAttributeCheckData | null;
            tags_data: TImportModalTagCheckData | null;
            contact_fields_data: {
                new: string[];
                over_limit_items: string[];
            } | null;
            project_fields_data: {
                new: string[];
                over_limit_items: string[];
            } | null;
        };
    };

    [ClientPendingTaskType.BatchGoogleContactsImport]: {
        taskData: TImportGoogleContactsTaskData;
        onUpdateProgress?: (progress: number) => void;
        returnType: {
            created_contact_ids: string[];
            failed_contacts_emails: string[];
            updated_contact_ids: string[];
        } | null;
    };

    [ClientPendingTaskType.BatchImport]: {
        taskData: TImportTaskData;
        onUpdateProgress: (progress: number) => void;
        returnType: {
            created_client_ids: string[];
            created_contact_cf_ids: string[] | null;
            created_project_cf_ids: string[] | null;
            created_project_ids: string[];
            failed_client_emails: string[];
            updated_client_ids: string[];
        } | null;
    };

    [ClientPendingTaskType.FetchGoogleContactsForImport]: {
        taskData: {};
        onUpdateProgress: undefined;
        returnType: TImportGoogleContactsData;
    };

    [ClientPendingTaskType.DeleteContacts]: {
        taskData: TDeleteContactsTaskData;
        onUpdateProgress: undefined;
        returnType: {
            successful: string[];
            failed: string[];
        };
    };

    [ClientPendingTaskType.DeleteWorkspaces]: {
        taskData: IDeleteWorkspacesTaskData;
        onUpdateProgress: undefined;
        returnType: {
            workspaces: {
                successful: string[];
                failed: string[];
            };
        };
    };

    [ClientPendingTaskType.GenerateInvoicePdf]: {
        taskData: IGenerateInvoicePdfTaskData;
        onUpdateProgress: undefined;
        returnType: any; // TODO: define return type;
    };

    [ClientPendingTaskType.BulkAddTagsToEntities]: {
        taskData: IBulkAddTags & TNewBulkActionTaskData;
        onUpdateProgress?: (props: any) => any;
        returnType: any;
    };

    [ClientPendingTaskType.BulkRemoveTagsFromEntities]: {
        taskData: IBulkRemoveTags & TNewBulkActionTaskData;
        onUpdateProgress?: (props: any) => any;
        returnType: any;
    };

    [ClientPendingTaskType.GenerateReport]: {
        taskData: TGenerateReportTaskData;
        onUpdateProgress: undefined;
        returnType: string;
    };

    [ClientPendingTaskType.PublishFlow]: {
        taskData: IShareFlowTaskData;
        onUpdateProgress: undefined;
        returnType: any; // TODO: define return type
    };

    [ClientPendingTaskType.AutoSuggestEdit]: {
        taskData: IAutoSuggestEditTaskData;
        onUpdateProgress: undefined;
        returnType: string;
    };

    [ClientPendingTaskType.AutoSuggest]: {
        taskData: IAutoSuggestTaskData;
        onUpdateProgress: undefined;
        returnType: string;
    };

    [ClientPendingTaskType.ShareFlow]: {
        taskData: IShareFlowTaskData;
        onUpdateProgress: undefined;
        returnType: {
            [recipientId: string]: {
                delivery: IDeliverySuccess | IDeliveryFail;
            };
        }; // TODO: properly finish this type
    };

    [ClientPendingTaskType.RefundPayment]: {
        taskData: IPaymentVariableRefunded;
        onUpdateProgress: undefined;
        returnType: any; // TODO: define return type
    };

    [ClientPendingTaskType.SendBatchEmail]: {
        taskData: ISendBatchEmailTaskData;
        onUpdateProgress: undefined;
        returnType: {
            batched: {
                count: number;
            };
        };
    };

    [ClientPendingTaskType.SendBulkEmailToWorkspaces]: {
        taskData: ISendBulkEmailToWorkspacesTaskData;
        onUpdateProgress: undefined;
        returnType: {
            workspaces: {
                failed: string[];
                successful: string[];
            };
        };
    };

    [ClientPendingTaskType.ArchiveWorkspaces]: {
        taskData: IArchiveWorkspacesTaskData;
        onUpdateProgress: undefined;
        returnType: {
            workspaces: {
                failed: string[];
                successful: string[];
            };
        };
    };

    [ClientPendingTaskType.MoveWorkspacesStage]: {
        taskData: IMoveWorkspacesStageTaskData;
        onUpdateProgress: undefined;
        returnType: {
            workspaces: {
                failed: string[];
                successful: string[];
            };
        };
    };

    [ClientPendingTaskType.CustomizePipelineStages]: {
        taskData: ICustomizePipelineStagesTaskData;
        onUpdateProgress: undefined;
        returnType: any; // TODO: define return type;
    };

    [ClientPendingTaskType.AssignWorkspaceToTeamMember]: {
        taskData: IAssignWorkspaceToTeamMemberTaskData;
        onUpdateProgress: undefined;
        returnType: {
            successful: boolean;
            failed: boolean;
        };
    };

    [ClientPendingTaskType.DeleteTeamMemberFromCompany]: {
        taskData: IDeleteTeamMemberFromCompanyTaskData;
        onUpdateProgress: undefined;
        returnType: boolean;
    };

    [ClientPendingTaskType.InitN8NUser]: {
        taskData: {};
        onUpdateProgress: undefined;
        returnType: boolean;
    };

    [ClientPendingTaskType.CreatePayLink]: {
        taskData: ICreatePayLinkRequest;
        onUpdateProgress: undefined;
        returnType: IPayLink;
    };

    [ClientPendingTaskType.DeletePayLink]: {
        taskData: IDeletePayLinkRequest;
        onUpdateProgress: undefined;
        returnType: IPayLink;
    };

    [ClientPendingTaskType.SendWorkspaceMessage]: {
        taskData: ISendWorkspaceMessageTaskData;
        onUpdateProgress: (props: any) => any;
        returnType: {
            workspaces?: {
                result: {
                    failed: boolean;
                    send_results: Array<
                        | {
                              success: true;
                          }
                        | {
                              recipient: {
                                  email: string;
                                  _id: string;
                                  full_name: string;
                              };
                              error: {
                                  type: string;
                                  title: string;
                                  message: string;
                              };
                              success: false;
                          }
                    >;
                };
            };
            canceled?: boolean;
        };
    };

    [ClientPendingTaskType.CreateNewPrismmOccasion]: {
        taskData: {
            event_id: string;
            occasion_details: ICreatePrismmOccasionDetails;
        };
        onUpdateProgress: undefined;
        returnType: IEventPrismmOccasion;
    };

    [ClientPendingTaskType.SendRescheduleProjectEmail]: {
        taskData: SendRescheduleProjectEmailData;
        onUpdateProgress: undefined;
        returnType: any;
    };

    [ClientPendingTaskType.SyncQbPayment]: {
        taskData: {
            _id: string;
            is_flow: boolean;
            force_invoice_creation?: boolean;
        };
        onUpdateProgress: undefined;
        returnType: any;
    };

    [ClientPendingTaskType.ResumeExecutionNode]: {
        taskData: {
            execution_id: string;
            step_id: string;
            payload?: {
                path?: boolean;
            };
        };
        onUpdateProgress: undefined;
        returnType: IResumeExecutionTaskResult; // The updated execution data from the server
    };
}
export type TDeleteContactsTaskData = IUserId &
    (IOldDeleteContactsTaskData | TNewBulkActionTaskData);

interface IUserId {
    user_id: string;
}

interface IOldDeleteContactsTaskData {
    contact_ids: string[];
}

interface IGenerateInvoicePdfTaskData {
    invoice_id: string;
    user_id: string;
    file_name: string;
}

export interface INewBulkActionExplicitIdsTaskData {
    selected_entities_ids: string[];
}

export interface INewBulkActionFilterBased {
    new_bulk_actions: {
        filters: Record<string, any>;
        ids_to_exclude?: string[];
    };
}

export type TNewBulkActionTaskData =
    | INewBulkActionExplicitIdsTaskData
    | INewBulkActionFilterBased;

interface IBulkAddTags {
    entity_type: 'contact' | 'workspace';
    selected_tag_ids: string[];
}

interface IBulkRemoveTags {
    entity_type: 'contact' | 'workspace';
    selected_tag_ids: string[];
}

interface ICustomizePipelineStagesTaskData {
    pipeline_stages: IUserPipelineStageResponse[];
    workspaces_adjustments: IWorkspaceAdjustment[];
    view_data?: {
        _id: string;
        show_groups: boolean;
        client_stage_ids: string[];
    };
}

type TGenerateReportTaskData =
    | IExportReportParams
    | ISummaryReportParams
    | IGenerateConnectionsReportTaskData
    | IArchivedWorkspacesReportTaskData;

interface IGenerateConnectionsReportTaskData {
    user_id: string;
    report_type: 'connections_report';
    filters?: TFilterTypes;
}

interface IArchivedWorkspacesReportTaskData {
    report_type: 'archived_workspaces_report';
}

export interface IShareFlowTaskData extends IEmailAttachmentProps {
    flow_id: string;
    recipients_ids?: string[];
    email?: Omit<IVendorEmail, keyof IEmailAttachmentProps>;
    is_preview?: boolean;
    scheduled_at?: Date;
    scheduled_send_date?: number;
    reason?: string;
}

interface IDeliverySuccess {
    status: 'success';
}
export interface IDeliveryFail {
    status: 'failure';
    error?: string;
    message?: string;
}
interface ISendBatchEmailTaskData extends IEmailAttachmentProps {
    user_id: string;
    recipient_user_ids: string[];
    subject: string;
    html_body: string;
    testEmail?: boolean;
}

interface IDeleteWorkspacesTaskData {
    workspaces: string[];
}

interface ISendBulkEmailToWorkspacesTaskData extends IEmailAttachmentProps {
    user_id: string;
    workspaces: string[];
    subject: string;
    html_body: string;
}

interface IArchiveWorkspacesTaskData {
    workspaces: { [workspaceId: string]: string };
    notifyMembers: boolean;
}

interface IMoveWorkspacesStageTaskData {
    workspaces: string[];
    stage: string;
    disable_tracking?: boolean;
}

export interface IAssignWorkspaceToTeamMemberTaskData {
    workspace_id: string;
    assignee_id: string;
    member_to_replace_id: string;
    assign_meetings: boolean;
}

export interface IDeleteTeamMemberFromCompanyTaskData {
    company_id: string;
    member_id: string;
    performing_user_id: string;
}

export interface ISendWorkspaceMessageTaskData {
    context_type?: 'contact_workspace';
    delay?: number;
    feed_to_reply_id?: string;
    force: boolean;
    general_files?: IEmailAttachment[];
    html_body: string;
    image_files?: IEmailAttachment[];
    pvl_id?: string;
    recipient_ids?: string[];
    scheduled_send_date?: number;
    subject: string;
    ws_id: string;
}
